import { removeBackground, Config, preload } from '@imgly/background-removal';
import { env } from 'onnxruntime-web';
import { ProcessingConfig, DeviceCapabilities } from '../../types/client';
import { debug } from '../../utils/debug';

export class BackgroundRemovalService {
  private isInitialized: boolean = false;
  private config: Config;
  private capabilities: DeviceCapabilities;
  private demoMode: boolean = false;

  constructor(capabilities: DeviceCapabilities, options?: { demoMode?: boolean }) {
    this.capabilities = capabilities;
    this.demoMode = options?.demoMode || false;
    this.config = this.createOptimalConfig(capabilities);
  }

  private createOptimalConfig(capabilities: DeviceCapabilities): Config {
    // Configure based on device capabilities
    const config: Config = {
      debug: process.env.NODE_ENV === 'development',
      // Remove publicPath to use default IMG.LY CDN hosting
      fetchArgs: {
        mode: 'cors',
        credentials: 'omit',
        cache: 'force-cache'
      },
      proxyToWorker: false, // Disable worker for better compatibility
      progress: (key, current, total) => {
        const progress = Math.round((current / total) * 100);
        debug.service(`📦 Loading ${key}: ${progress}%`);
      }
    };

    // Use higher quality model based on device capabilities
    if (capabilities.availableMemory >= 4 && capabilities.webglSupported) {
      config.model = 'isnet'; // Higher quality model for capable devices
      debug.log('🎯 Using high-quality model: isnet');
    } else {
      config.model = 'isnet_quint8'; // Smaller model for lower-end devices
      debug.log('📱 Using quantized model for compatibility: isnet_quint8');
    }

    return config;
  }

  async initialize(onProgress?: (progress: number) => void): Promise<void> {
    if (this.isInitialized) return;

    // Demo mode - skip actual initialization
    if (this.demoMode) {
      debug.log('📺 Initializing background removal service in demo mode...');
      if (onProgress) onProgress(0);
      
      // Simulate loading
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (onProgress) onProgress(100);
      this.isInitialized = true;
      debug.log('✓ Background removal service initialized in demo mode');
      return;
    }

    try {
      debug.log('🔄 Initializing background removal service...');
      debug.log('  - Device capabilities:', this.capabilities);
      debug.log('  - Using model:', this.config.model);
      debug.log('  - Configuration:', this.config);

      // Configure ONNX Runtime environment
      debug.log('⚙️ Configuring ONNX Runtime environment...');
      
      // Let the library handle ONNX runtime configuration automatically
      // The background-removal library will manage WASM paths internally
      env.wasm.numThreads = Math.min(4, navigator.hardwareConcurrency || 4);
      env.wasm.simd = true;
      
      debug.log('  - ONNX wasm config:', {
        numThreads: env.wasm.numThreads,
        simd: env.wasm.simd,
        note: 'WASM paths managed by background-removal library'
      });

      if (onProgress) onProgress(0);

      // Add timeout for model loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Model loading timeout (60s)')), 60000);
      });

      // Preload the model with timeout
      debug.log('📡 Starting model preload from CDN...');
      await Promise.race([
        preload(this.config),
        timeoutPromise
      ]);

      if (onProgress) onProgress(100);

      this.isInitialized = true;
      debug.log('✓ Background removal service initialized successfully');

    } catch (error) {
      this.isInitialized = false;
      debug.error('❌ Failed to initialize background removal service:', error);
      debug.error('Full error details:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Background removal initialization failed';
      
      if (error instanceof Error) {
        if (error.message.includes('_OrtGetInputOutputMetadata')) {
          errorMessage = 'ONNX Runtime initialization failed - this may be due to CORS or CDN access issues. Using demo mode instead.';
        } else if (error.message.includes('fetch')) {
          errorMessage = 'Failed to download AI model from CDN. Please check your internet connection.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Model loading timed out. Please check your internet connection.';
        } else {
          errorMessage = `Background removal failed: ${error.message}`;
        }
      }
      
      throw new Error(errorMessage);
    }
  }

  async removeBackground(
    imageData: ImageData | HTMLImageElement | File | Blob,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    if (!this.isInitialized) {
      await this.initialize(onProgress ? (p) => onProgress(p * 0.2) : undefined);
    }

    // Demo mode - simulate background removal with actual transparency
    if (this.demoMode) {
      debug.log('📺 Demo mode: simulating background removal with transparency...');
      const startTime = performance.now();
      
      if (onProgress) onProgress(20);
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (onProgress) onProgress(60);
      
      // Create a canvas to simulate background removal
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      
      // Convert input to image element for processing
      let img: HTMLImageElement;
      if (imageData instanceof File || imageData instanceof Blob) {
        img = await this.blobToImage(imageData);
      } else if (imageData instanceof HTMLImageElement) {
        img = imageData;
      } else {
        // Convert ImageData to image
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d')!;
        tempCanvas.width = imageData.width;
        tempCanvas.height = imageData.height;
        tempCtx.putImageData(imageData, 0, 0);
        img = await this.blobToImage(await new Promise<Blob>(resolve => tempCanvas.toBlob(resolve as any)));
      }
      
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Draw the original image
      ctx.drawImage(img, 0, 0);
      
      if (onProgress) onProgress(80);
      
      // Create a more dramatic background removal simulation
      const imageDataObj = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageDataObj.data;
      
      // More aggressive background removal simulation with yielding
      const totalPixels = data.length / 4;
      const pixelsPerChunk = 1000; // Process 1000 pixels at a time
      
      for (let chunkStart = 0; chunkStart < totalPixels; chunkStart += pixelsPerChunk) {
        const chunkEnd = Math.min(chunkStart + pixelsPerChunk, totalPixels);
        
        // Process chunk of pixels
        for (let pixel = chunkStart; pixel < chunkEnd; pixel++) {
          const i = pixel * 4;
          const x = pixel % canvas.width;
          const y = Math.floor(pixel / canvas.width);
          
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const brightness = (r + g + b) / 3;
          
          // Calculate distance from center (where subject is likely to be)
          const centerX = canvas.width / 2;
          const centerY = canvas.height / 2;
          const distanceFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
          const maxDistance = Math.sqrt(centerX ** 2 + centerY ** 2);
          const normalizedDistance = distanceFromCenter / maxDistance;
          
          // More aggressive edge removal - remove outer 40% of image
          if (normalizedDistance > 0.6) {
            data[i + 3] = 0; // Completely transparent
          }
          // Fade out area between 40%-60% from center
          else if (normalizedDistance > 0.4) {
            const fadeAmount = (normalizedDistance - 0.4) / 0.2; // 0 to 1
            data[i + 3] = data[i + 3] * (1 - fadeAmount);
          }
          // Remove light/white backgrounds more aggressively
          else if (brightness > 180) {
            data[i + 3] = data[i + 3] * 0.1; // Almost completely transparent
          }
          // Remove medium-bright areas partially
          else if (brightness > 140) {
            data[i + 3] = data[i + 3] * 0.5; // 50% transparent
          }
          
          // Also remove areas that are very uniform in color (likely background)
          const colorVariance = Math.abs(r - g) + Math.abs(g - b) + Math.abs(r - b);
          if (colorVariance < 30 && brightness > 100) {
            data[i + 3] = data[i + 3] * 0.2; // Make uniform areas very transparent
          }
        }
        
        // Yield control back to the browser every chunk
        if (chunkEnd < totalPixels) {
          await new Promise(resolve => setTimeout(resolve, 0));
          
          // Update progress based on chunk completion
          const chunkProgress = chunkEnd / totalPixels;
          if (onProgress) onProgress(80 + chunkProgress * 15);
        }
      }
      
      ctx.putImageData(imageDataObj, 0, 0);
      
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob(resolve as any, 'image/png'); // PNG to preserve transparency
      });
      
      const processingTime = performance.now() - startTime;
      debug.log(`📺 Demo background removal with transparency completed in ${processingTime.toFixed(2)}ms`);
      debug.log(`✨ Background removal created transparent areas - check the result with the checkered background pattern`);
      
      if (onProgress) onProgress(100);
      return blob!;
    }

    try {
      debug.log('🎨 Removing background...');
      const startTime = performance.now();

      if (onProgress) onProgress(20);

      // Configure progress callback
      const progressConfig = {
        ...this.config,
        progress: (key: string, current: number, total: number) => {
          if (onProgress) {
            const baseProgress = 20; // Initial 20% for initialization
            const processingProgress = (current / total) * 80; // Remaining 80% for processing
            onProgress(baseProgress + processingProgress);
          }
        }
      };

      // Remove background
      const result = await removeBackground(imageData, progressConfig);

      const processingTime = performance.now() - startTime;
      debug.log(`✓ Background removal completed in ${processingTime.toFixed(2)}ms`);

      if (onProgress) onProgress(100);

      return result;

    } catch (error) {
      debug.error('❌ Background removal failed:', error);
      throw new Error(`Background removal failed: ${error}`);
    }
  }

  private async convertToBlob(imageData: ImageData | HTMLImageElement): Promise<Blob> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      
      if (imageData instanceof ImageData) {
        canvas.width = imageData.width;
        canvas.height = imageData.height;
        ctx.putImageData(imageData, 0, 0);
      } else {
        canvas.width = imageData.naturalWidth;
        canvas.height = imageData.naturalHeight;
        ctx.drawImage(imageData, 0, 0);
      }
      
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, 'image/png');
    });
  }

  private async blobToImage(blob: Blob | File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(blob);
    });
  }

  async removeBackgroundWithFallback(
    imageData: ImageData | HTMLImageElement | File | Blob,
    onProgress?: (progress: number) => void
  ): Promise<Blob> {
    try {
      // Try with the configured model first
      return await this.removeBackground(imageData, onProgress);
    } catch (error) {
      debug.warn('⚠️ Primary background removal failed, trying fallback...');
      
      try {
        // Fallback to simpler configuration
        const fallbackConfig: Config = {
          ...this.config,
          model: 'isnet_quint8', // Use smallest model as fallback
          debug: false
        };

        // Reset initialization for fallback
        this.isInitialized = false;
        this.config = fallbackConfig;

        return await this.removeBackground(imageData, onProgress);
      } catch (fallbackError) {
        debug.error('❌ Fallback background removal also failed:', fallbackError);
        throw new Error(`Background removal failed: ${fallbackError}`);
      }
    }
  }

  // Convert various image formats to ImageData for processing
  static async imageToImageData(image: HTMLImageElement | File | Blob): Promise<ImageData> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      if (image instanceof HTMLImageElement) {
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        ctx.drawImage(image, 0, 0);
        resolve(ctx.getImageData(0, 0, canvas.width, canvas.height));
      } else {
        // Handle File or Blob
        const url = URL.createObjectURL(image);
        const img = new Image();
        
        img.onload = () => {
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          resolve(imageData);
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image'));
        };
        
        img.src = url;
      }
    });
  }

  // Convert Blob to ImageData
  static async blobToImageData(blob: Blob): Promise<ImageData> {
    const url = URL.createObjectURL(blob);
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        resolve(imageData);
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image from blob'));
      };
      
      img.src = url;
    });
  }

  // Convert ImageData to Blob
  static async imageDataToBlob(imageData: ImageData, format: 'png' | 'jpeg' = 'png', quality?: number): Promise<Blob> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      ctx.putImageData(imageData, 0, 0);
      
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, `image/${format}`, quality);
    });
  }

  isReady(): boolean {
    return this.isInitialized;
  }

  getConfig(): Config {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<Config>): void {
    this.config = { ...this.config, ...newConfig };
    this.isInitialized = false; // Force re-initialization with new config
  }

  dispose(): void {
    this.isInitialized = false;
    debug.log('🧹 Background removal service disposed');
  }
}